import { Check } from 'lucide-react';

import { Spinner } from '../../design-system';
import { PublicBrandShell } from '../../components/public/PublicBranding';
import { PricingTier, usePublicPricingTiers } from '../../hooks/usePublicPricingTiers';
import { IN_APP_FOOTER_LINKS } from './publicFooterLinks';

// Prices must match the Paddle catalog exactly. Amounts come from the same DB
// subscription_tiers that back the Paddle prices (seed_subscription_tiers.py + the
// PADDLE_PRICE_ID_* env vars). Plan set + content mirror kolega.dev.
const TAX_NOTE = 'Prices in USD. Taxes may apply and will be calculated at checkout.';
// The 7-day trial is free platform access enforced in-app (no card, no charge). Paddle is
// only used when a customer subscribes after the trial, at the monthly price shown below.
const TRIAL_NOTE =
  'New to Kolega? Start with a 7-day free trial — full access, no card required. ' +
  'When you subscribe, you are billed monthly at the plan price shown (charged at checkout).';

interface DisplayPlan {
  name: string;
  description: string;
  priceLabel: string;
  cadence: string;
  includes?: string;
  features: string[];
  cta: { label: string; href: string };
  featured?: boolean;
}

// Free and Enterprise are display-only (not self-serve DB tiers); mirror kolega.dev.
const FREE_PLAN: DisplayPlan = {
  name: 'Free',
  description: 'Try it on one repo.',
  priceLabel: '$0',
  cadence: '/month',
  features: ['1 application', '250k LOC', 'Scheduled scans', '0 autofix PRs'],
  cta: { label: 'Start free', href: '/sign-up' },
};

const ENTERPRISE_PLAN: DisplayPlan = {
  name: 'Enterprise',
  description: 'For organizations with security teams and procurement.',
  priceLabel: 'Custom',
  cadence: '',
  includes: 'Pro',
  features: [
    'Multiple apps + continuous scanning',
    'Vulnerability exploitation testing',
    'SSO/SAML + audit logs + SIEM',
    'Self-hosted runners',
    'Dedicated Slack + SLA',
  ],
  cta: { label: 'Contact Sales', href: '/contact' },
};

function tierToPlan(tier: PricingTier): DisplayPlan {
  return {
    name: tier.display_name,
    description: tier.description || '',
    priceLabel: `$${tier.price}`,
    cadence: '/month',
    includes: tier.metadata?.includes,
    features: tier.features,
    cta: { label: 'Get started', href: '/sign-up' },
    featured: tier.name === 'pro',
  };
}

function PlanCard({ plan }: { plan: DisplayPlan }) {
  return (
    <div
      className={`relative flex h-full flex-col rounded-xl border p-6 ${
        plan.featured ? 'border-[#ADDD30]/60 bg-[#ADDD30]/5' : 'border-[#1F1F1F] bg-[#0A0D07]'
      }`}
    >
      {plan.featured && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="rounded-full bg-[#ADDD30] px-3 py-1 text-xs font-medium text-black">Most Popular</span>
        </div>
      )}

      <h3 className="text-xl font-bold text-white">{plan.name}</h3>
      <p className="mt-2 min-h-[3rem] text-sm text-[#7E8473]">{plan.description}</p>

      <div className="mt-4 flex items-baseline gap-1">
        <span className="text-4xl font-extrabold text-white">{plan.priceLabel}</span>
        {plan.cadence && <span className="text-sm text-[#5A5E50]">{plan.cadence}</span>}
      </div>
      {plan.priceLabel.startsWith('$') && plan.priceLabel !== '$0' && (
        <p className="mt-1 text-xs text-[#666666]">USD, billed monthly. Taxes calculated at checkout.</p>
      )}

      {plan.includes ? (
        <p className="mt-6 border-t border-[#1F1F1F] pt-5 text-[11px] font-semibold uppercase tracking-wide text-[#5A5E50]">
          Everything in <span className="text-[#ADDD30]">{plan.includes}</span>, plus:
        </p>
      ) : (
        <div className="mt-6 border-t border-[#1F1F1F] pt-5" />
      )}

      <ul className="mt-4 flex-grow space-y-3">
        {plan.features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm text-[#A8AE9E]">
            <Check size={16} className="mt-0.5 flex-shrink-0 text-[#ADDD30]" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <a
        href={plan.cta.href}
        className={`mt-6 block rounded-lg px-4 py-3 text-center text-sm font-semibold transition-colors ${
          plan.featured
            ? 'bg-[#ADDD30] text-black hover:bg-[#9BC72B]'
            : 'border border-[#2C3420] text-white hover:border-[#ADDD30] hover:text-[#ADDD30]'
        }`}
      >
        {plan.cta.label}
      </a>
    </div>
  );
}

export default function PricingPage() {
  const { tiers, loading, error } = usePublicPricingTiers();

  // Only self-serve, sellable tiers (active + signup enabled). Grandfathered tiers like
  // Team (signup_enabled=false) are hidden here, matching kolega.dev.
  const sellableTiers = (tiers || [])
    .filter((t) => t.is_active && t.metadata?.signup_enabled !== false && !t.metadata?.requires_sales_contact)
    .map(tierToPlan);

  const plans: DisplayPlan[] = [FREE_PLAN, ...sellableTiers, ENTERPRISE_PLAN];

  return (
    <PublicBrandShell footerLinks={IN_APP_FOOTER_LINKS}>
      <div className="min-h-screen bg-[#0B0E08]">
        <main className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <h1 className="text-3xl font-bold text-white sm:text-4xl">Pricing</h1>
            <p className="mx-auto mt-3 max-w-2xl text-[#A8AE9E]">
              Kolega.dev — autonomous security scanning and remediation. Start with the full Pro plan for 7 days.
            </p>
            <p className="mt-4 text-sm text-[#808080]">{TAX_NOTE}</p>
            <p className="mx-auto mt-2 max-w-2xl text-sm text-[#808080]">{TRIAL_NOTE}</p>
          </div>

          {loading && (
            <div className="flex justify-center py-16">
              <Spinner size={32} />
            </div>
          )}

          {error && !loading && (
            <p className="py-16 text-center text-[#A8AE9E]">Unable to load pricing right now. Please try again later.</p>
          )}

          {!loading && !error && (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {plans.map((plan) => (
                <PlanCard key={plan.name} plan={plan} />
              ))}
            </div>
          )}
        </main>
      </div>
    </PublicBrandShell>
  );
}
